import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  LockOutlined,
  SwapOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import dayjs from 'dayjs';
import PermissionGuard from '../components/PermissionGuard';

const { Title } = Typography;
const { Option } = Select;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [transferringUser, setTransferringUser] = useState(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [transferForm] = Form.useForm();

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      const userData = response.data?.data || response.data || [];
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('获取用户列表失败');
      setUsers([]);
    }
    setLoading(false);
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/users/roles/list');
      const roleData = response.data || [];
      setRoles(Array.isArray(roleData) ? roleData : []);
    } catch (error) {
      console.error('获取角色列表失败:', error);
      setRoles([]);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      message.success('删除成功');
      fetchUsers();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, values);
        message.success('更新成功');
      } else {
        await api.post('/users', values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (error) {
      message.error(editingUser ? '更新失败' : '创建失败');
    }
  };

  const handleChangePassword = (record) => {
    setChangingPasswordUser(record);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handlePasswordSubmit = async (values) => {
    try {
      await api.put(`/users/${changingPasswordUser.id}/password`, {
        password: values.password
      });
      message.success('密码修改成功');
      setPasswordModalVisible(false);
    } catch (error) {
      message.error('密码修改失败');
    }
  };

  const handleTransfer = (record) => {
    setTransferringUser(record);
    transferForm.resetFields();
    setTransferModalVisible(true);
  };

  const handleTransferSubmit = async (values) => {
    try {
      const response = await api.post(`/users/${transferringUser.id}/transfer-leads`, {
        new_user_id: values.new_user_id
      });
      
      if (response.data.transferred_count === 0) {
        message.info(response.data.message);
      } else {
        message.success(
          `成功转交 ${response.data.transferred_count} 份简历：${response.data.from_user} → ${response.data.to_user}`
        );
      }
      
      setTransferModalVisible(false);
    } catch (error) {
      message.error(error.response?.data?.message || '简历转交失败');
    }
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        const roleMap = {
          super_admin: { text: '超级管理员', color: 'red' },
          hr_manager: { text: '人事主管', color: 'blue' },
          hr_assistant: { text: '人事助理', color: 'green' }
        };
        const config = roleMap[role] || { text: role, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '最后登录',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <PermissionGuard permission="user.edit">
            <Button
              type="link"
              icon={<SwapOutlined />}
              onClick={() => handleTransfer(record)}
              title="转交简历"
            />
          </PermissionGuard>
          <PermissionGuard permission="user.edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              title="编辑用户"
            />
          </PermissionGuard>
          <PermissionGuard permission="user.edit">
            <Button
              type="link"
              icon={<LockOutlined />}
              onClick={() => handleChangePassword(record)}
              title="修改密码"
            />
          </PermissionGuard>
          <PermissionGuard permission="user.delete">
            {(record.role === 'admin' || record.role === 'super_admin') ? (
              <Button 
                type="link" 
                disabled 
                icon={<DeleteOutlined />}
                title="管理员用户不能删除"
                style={{ color: '#ccc' }}
              >
                删除
              </Button>
            ) : (
              <Popconfirm
                title="确定要删除这个用户吗？"
                onConfirm={() => handleDelete(record.id)}
              >
                <Button 
                  type="link" 
                  danger 
                  icon={<DeleteOutlined />}
                  title="删除用户"
                />
              </Popconfirm>
            )}
          </PermissionGuard>
          <PermissionGuard permission="permission.assign">
            <Button
              type="link"
              icon={<SettingOutlined />}
              onClick={() => window.location.href = `/role-permissions?role=${record.role}`}
              title="权限设置"
            />
          </PermissionGuard>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card 
        title="用户管理" 
        extra={
          <PermissionGuard permission="user.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              添加用户
            </Button>
          </PermissionGuard>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      <Modal
        title={editingUser ? '编辑用户' : '添加用户'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="username"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select>
              {roles.map(role => (
                <Option key={role.name} value={role.name}>
                  {role.display_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改密码Modal */}
      <Modal
        title={`修改密码 - ${changingPasswordUser?.username || ''}`}
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
        >
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度至少6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认修改
              </Button>
              <Button onClick={() => setPasswordModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 转交简历Modal */}
      <Modal
        title={`转交简历 - ${transferringUser?.username || ''}`}
        open={transferModalVisible}
        onCancel={() => setTransferModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={transferForm}
          layout="vertical"
          onFinish={handleTransferSubmit}
        >
          <Form.Item
            name="new_user_id"
            label="选择接收简历的员工"
            rules={[{ required: true, message: '请选择接收简历的员工' }]}
          >
            <Select
              placeholder="请选择员工"
              showSearch
              filterOption={(input, option) =>
                (option?.children?.toLowerCase() || '').indexOf(input.toLowerCase()) >= 0
              }
            >
              {users
                .filter(u => u.id !== transferringUser?.id && u.is_active)
                .map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.username}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <div style={{ marginTop: '24px', padding: '12px', background: '#f0f0f0', borderRadius: '4px' }}>
            <Typography.Text type="warning">
              提示：此操作将把该员工名下的所有CRM简历转交给所选员工，请确认操作。
            </Typography.Text>
          </div>

          <Form.Item style={{ marginTop: '24px' }}>
            <Space>
              <Button type="primary" htmlType="submit" danger>
                确认转交
              </Button>
              <Button onClick={() => setTransferModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users; 